import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../api';

// Define types
export interface Vote {
  id: string;
  questionId: string;
  userId: string;
  optionIndex: number;
  timestamp: string;
}

export interface VoteResult {
  questionId: string;
  optionCounts: number[];
  totalVotes: number;
}

interface VotingState {
  userVotes: Record<string, Vote>; // questionId -> Vote
  results: Record<string, VoteResult>; // questionId -> VoteResult
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: VotingState = {
  userVotes: {},
  results: {},
  loading: false,
  error: null,
};

// Create slice
const votingSlice = createSlice({
  name: 'voting',
  initialState,
  reducers: {
    setUserVote: (state, action: PayloadAction<Vote>) => {
      const vote = action.payload;
      state.userVotes[vote.questionId] = vote;
    },
    clearUserVotes: (state) => {
      state.userVotes = {};
    },
    setVoteResult: (state, action: PayloadAction<VoteResult>) => {
      const result = action.payload;
      state.results[result.questionId] = result;
    },
    clearVoteResults: (state) => {
      state.results = {};
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Handle API responses
    builder.addMatcher(
      api.endpoints.submitVote.matchPending,
      (state) => {
        state.loading = true;
        state.error = null;
      }
    );
    builder.addMatcher(
      api.endpoints.submitVote.matchFulfilled,
      (state, { payload }) => {
        state.loading = false;
        // The API might return the updated vote and/or results
        if (payload.vote) {
          state.userVotes[payload.vote.questionId] = payload.vote;
        }
        if (payload.result) {
          state.results[payload.result.questionId] = payload.result;
        }
      }
    );
    builder.addMatcher(
      api.endpoints.submitVote.matchRejected,
      (state, { payload }) => {
        state.loading = false;
        state.error = (payload as any)?.data?.message || 'Failed to submit vote';
      }
    );
    
    // Handle vote results fetching
    builder.addMatcher(
      api.endpoints.getVotes.matchFulfilled,
      (state, { payload }) => {
        if (payload.result) {
          state.results[payload.result.questionId] = payload.result;
        }
      }
    );
  },
});

export const {
  setUserVote,
  clearUserVotes,
  setVoteResult,
  clearVoteResults,
  setLoading,
  setError,
  clearError,
} = votingSlice.actions;

export default votingSlice.reducer;
